<template>
  <main class="page search-page">
    <div class="search-page__container">
      <!-- Search bar -->
      <div class="search-bar">
        <div class="search-bar__icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        </div>
        <input
          ref="searchInput"
          v-model.trim="query"
          type="text"
          placeholder="Search regulatory content..."
          class="search-bar__input"
          @keyup.enter="doSearch"
        />
        <div class="search-bar__shortcut">
          <kbd>Ctrl</kbd> <kbd>K</kbd>
        </div>
      </div>

      <!-- Suggestion pills -->
      <div v-if="!hasResults && !loading" class="suggestion-pills">
        <button
          v-for="suggestion in defaultSuggestions"
          :key="suggestion"
          class="suggestion-pill"
          @click="useSuggestion(suggestion)"
        >
          {{ suggestion }}
        </button>
      </div>

      <!-- Refinement section (collapsed by default) -->
      <div
        v-if="refinements.length > 0 && hasResults"
        class="refinement-section"
      >
        <div class="refinement-header" @click="refinementsOpen = !refinementsOpen">
          <span class="refinement-header__icon">&#10024;</span>
          <span class="refinement-header__title">Narrow your search</span>
          <span class="refinement-header__subtitle">
            Your current search term is quite broad &mdash; try a more targeted query
          </span>
          <button class="refinement-header__toggle" :aria-label="refinementsOpen ? 'Collapse' : 'Expand'">
            <svg :class="{ 'is-open': refinementsOpen }" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 4.5 L6 7.5 L9 4.5"/></svg>
          </button>
          <button class="refinement-header__close" @click.stop="dismissRefinements">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 2l8 8M10 2l-8 8"/></svg>
          </button>
        </div>
        <transition name="slide">
          <div v-if="refinementsOpen" class="refinement-cards">
            <div
              v-for="(ref, idx) in refinements"
              :key="idx"
              class="refinement-card"
              @click="useRefinement(ref)"
            >
              <div class="refinement-card__indicator" :class="'refinement-card__indicator--' + refineColor(idx)" />
              <div class="refinement-card__content">
                <div class="refinement-card__label">
                  {{ ref.label || ref.dimension || 'Suggestion' }}
                </div>
                <div class="refinement-card__query">
                  {{ ref.query || ref }}
                </div>
              </div>
              <div class="refinement-card__action">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Facet pills -->
      <div v-if="hasResults" class="facet-section">
        <div v-if="facets.type.length" class="facet-row">
          <span class="facet-row__label">Type</span>
          <div class="facet-row__pills">
            <button
              v-for="f in facets.type"
              :key="'type-' + f.name"
              class="facet-pill"
              :class="{ 'facet-pill--active': activeFilters.type === f.name }"
              @click="toggleFilter('type', f.name)"
            >
              {{ formatContentType(f.name) }}
              <span class="facet-pill__count">{{ f.count }}</span>
            </button>
          </div>
        </div>

        <div v-if="facets.jurisdiction.length" class="facet-row">
          <span class="facet-row__label">Jurisdiction</span>
          <div class="facet-row__pills">
            <button
              v-for="f in visibleJurisdictions"
              :key="'jur-' + f.name"
              class="facet-pill"
              :class="{ 'facet-pill--active': activeFilters.jurisdiction === f.name }"
              @click="toggleFilter('jurisdiction', f.name)"
            >
              {{ f.name }}
              <span class="facet-pill__count">{{ f.count }}</span>
            </button>
            <button
              v-if="facets.jurisdiction.length > 6"
              class="facet-pill facet-pill--more"
              @click="showAllJurisdictions = !showAllJurisdictions"
            >
              {{ showAllJurisdictions ? 'Show less' : `+${facets.jurisdiction.length - 6} more` }}
            </button>
          </div>
        </div>

        <div v-if="facets.source.length" class="facet-row">
          <span class="facet-row__label">Source</span>
          <div class="facet-row__pills">
            <button
              v-for="f in facets.source"
              :key="'src-' + f.name"
              class="facet-pill"
              :class="{ 'facet-pill--active': activeFilters.source === f.name }"
              @click="toggleFilter('source', f.name)"
            >
              {{ formatSource(f.name) }}
              <span class="facet-pill__count">{{ f.count }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Results header -->
      <div v-if="hasResults" class="results-header">
        <div class="results-header__count">
          <strong>{{ accessibleCount }}</strong> accessible
          <span v-if="lockedCount > 0" class="results-header__locked">
            <strong>{{ lockedCount }}</strong> locked
          </span>
        </div>
        <div class="results-header__sort">
          <button
            v-for="s in sortOptions"
            :key="s.value"
            class="sort-btn"
            :class="{ 'sort-btn--active': sortBy === s.value }"
            @click="sortBy = s.value"
          >
            {{ s.label }}
          </button>
        </div>
      </div>

      <!-- Loading skeletons -->
      <div v-if="loading" class="skeleton-list">
        <div v-for="i in 6" :key="i" class="skeleton-card">
          <div class="skeleton-card__badges">
            <span class="skeleton-line" style="width: 80px; height: 18px; border-radius: 12px;" />
            <span class="skeleton-line" style="width: 60px; height: 18px; border-radius: 12px;" />
            <span class="skeleton-line" style="width: 90px; height: 18px; border-radius: 12px;" />
          </div>
          <div class="skeleton-line" style="width: 75%; height: 16px; margin-bottom: 10px;" />
          <div class="skeleton-line" style="width: 100%; height: 12px; margin-bottom: 6px;" />
          <div class="skeleton-line" style="width: 66%; height: 12px;" />
          <div class="skeleton-line" style="width: 33%; height: 6px; border-radius: 3px; margin-top: 12px;" />
        </div>
      </div>

      <!-- Results list -->
      <div v-if="hasResults && !loading" class="results-list">
        <div
          v-for="article in paginatedResults"
          :key="article.id"
          class="result-card"
          :class="{ 'result-card--blocked': !article.accessible }"
        >
          <!-- Row 1: Meta -->
          <div class="result-card__meta">
            <span class="result-card__type">
              {{ formatContentType(article.content_type) }}
            </span>
            <span
              v-if="article.source_type"
              class="result-card__source"
              :class="'result-card__source--' + article.source_type"
            >
              {{ formatSource(article.source_type) }}
            </span>
            <span v-if="article.jurisdiction" class="result-card__jurisdiction">
              {{ article.jurisdiction }}
            </span>
          </div>

          <!-- Row 2: Title -->
          <a
            v-if="article.accessible && article.platform_url"
            :href="article.platform_url"
            class="result-card__title"
            target="_blank"
            rel="noopener"
          >
            {{ article.title }}
          </a>
          <span v-else class="result-card__title result-card__title--blocked">
            {{ article.title }}
          </span>

          <!-- Row 3: Summary -->
          <p v-if="article.summary && article.accessible" class="result-card__summary">
            {{ article.summary }}
          </p>
          <p v-else-if="article.summary && !article.accessible" class="result-card__summary result-card__summary--blurred">
            <span>{{ article.summary.slice(0, 80) }}</span><span class="blur-text">{{ article.summary.slice(80) }}</span>
          </p>

          <!-- Row 4: Footer with score + date + icon -->
          <div class="result-card__footer">
            <div class="result-card__score-row">
              <div class="score-bar">
                <div
                  class="score-bar__fill"
                  :class="{ 'score-bar__fill--blocked': !article.accessible }"
                  :style="{ width: scorePercent(article.score) + '%' }"
                />
              </div>
              <span class="score-bar__label">{{ scorePercent(article.score) }}%</span>
            </div>
            <span v-if="article.published_date" class="result-card__date">
              {{ formatDate(article.published_date) }}
            </span>
            <!-- Lock or arrow icon -->
            <svg v-if="!article.accessible" class="result-card__icon result-card__icon--lock" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <svg v-else class="result-card__icon result-card__icon--arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </div>

          <!-- Upsell banner -->
          <div v-if="!article.accessible && article.upsell" class="result-card__upsell">
            <span v-if="article.upsell.reason === 'upgrade_tier'" class="result-card__upsell-text">
              Requires <strong>{{ article.upsell.required_tier }}</strong> tier
            </span>
            <span v-else-if="article.upsell.reason === 'add_jurisdiction'" class="result-card__upsell-text">
              Requires <strong>{{ article.upsell.required_jurisdiction }}</strong> jurisdiction
            </span>
            <span class="result-card__upsell-badge">Upgrade</span>
          </div>
        </div>
      </div>

      <!-- Load more -->
      <div v-if="hasMoreResults" class="load-more">
        <button class="load-more__btn" @click="loadMore">
          Show more results
        </button>
      </div>

      <!-- No results -->
      <div v-if="!loading && searchPerformed && !hasResults" class="no-results">
        <p>No results found for &ldquo;<strong>{{ lastQuery }}</strong>&rdquo;</p>
        <p>Try broadening your search or using different keywords.</p>
      </div>
    </div>
  </main>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "SearchV2",
  data() {
    return {
      query: "",
      lastQuery: "",
      searchPerformed: false,
      sortBy: "relevance",
      activeFilters: {
        type: null,
        jurisdiction: null,
        source: null,
      },
      showAllJurisdictions: false,
      refinementsOpen: false,
      refinementsDismissed: false,
      displayCount: 20,
      sortOptions: [
        { label: "Relevance", value: "relevance" },
        { label: "Newest", value: "date" },
        { label: "By Type", value: "type" },
      ],
      defaultSuggestions: [
        "PSD2 strong customer authentication",
        "AML compliance for payment institutions",
        "Cross-border payment regulations",
        "Open banking requirements",
      ],
    };
  },
  head() {
    return { title: "VIXIO PaymentsCompliance - Search" };
  },
  computed: {
    ...mapState("Search", [
      "semanticResults",
      "semanticLoading",
      "semanticTotalAccessible",
      "semanticTotalUpsell",
      "refinements",
      "refinementsLoading",
    ]),
    loading() {
      return this.semanticLoading;
    },
    hasResults() {
      return this.semanticResults && this.semanticResults.length > 0;
    },
    accessibleResults() {
      return (this.semanticResults || []).filter((r) => r.accessible);
    },
    facets() {
      const results = this.accessibleResults;
      const af = this.activeFilters;

      const applyOtherFilters = (exclude) => {
        let filtered = results;
        if (exclude !== "type" && af.type) {
          filtered = filtered.filter((r) => (r.content_type || r.type) === af.type);
        }
        if (exclude !== "jurisdiction" && af.jurisdiction) {
          filtered = filtered.filter((r) => r.jurisdiction === af.jurisdiction);
        }
        if (exclude !== "source" && af.source) {
          filtered = filtered.filter((r) => r.source_type === af.source);
        }
        return filtered;
      };

      const countField = (items, fn) => {
        const counts = {};
        items.forEach((r) => {
          const val = fn(r);
          counts[val] = (counts[val] || 0) + 1;
        });
        return Object.entries(counts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
      };

      return {
        type: countField(applyOtherFilters("type"), (r) => r.content_type || r.type || "unknown"),
        jurisdiction: countField(applyOtherFilters("jurisdiction"), (r) => r.jurisdiction || "Unknown"),
        source: countField(applyOtherFilters("source"), (r) => r.source_type || "unknown"),
      };
    },
    filteredResults() {
      let results = (this.semanticResults || []).slice();

      if (this.activeFilters.type) {
        results = results.filter(
          (r) => (r.content_type || r.type) === this.activeFilters.type
        );
      }
      if (this.activeFilters.jurisdiction) {
        results = results.filter(
          (r) => r.jurisdiction === this.activeFilters.jurisdiction
        );
      }
      if (this.activeFilters.source) {
        results = results.filter(
          (r) => r.source_type === this.activeFilters.source
        );
      }

      if (this.sortBy === "date") {
        results.sort(
          (a, b) =>
            new Date(b.published_date || 0) - new Date(a.published_date || 0)
        );
      } else if (this.sortBy === "type") {
        results.sort((a, b) => {
          const typeA = a.content_type || a.type || "";
          const typeB = b.content_type || b.type || "";
          if (typeA !== typeB) return typeA.localeCompare(typeB);
          return (b.score || 0) - (a.score || 0);
        });
      }

      return results;
    },
    paginatedResults() {
      return this.filteredResults.slice(0, this.displayCount);
    },
    hasMoreResults() {
      return this.filteredResults.length > this.displayCount;
    },
    accessibleCount() {
      return this.filteredResults.filter((r) => r.accessible !== false).length;
    },
    lockedCount() {
      return this.filteredResults.filter((r) => r.accessible === false).length;
    },
    visibleJurisdictions() {
      if (this.showAllJurisdictions) return this.facets.jurisdiction;
      return this.facets.jurisdiction.slice(0, 6);
    },
    maxScore() {
      if (!this.semanticResults || this.semanticResults.length === 0) return 1;
      return Math.max(...this.semanticResults.map((r) => r.score || 0), 0.01);
    },
  },
  watch: {
    "$route.query.query": {
      immediate: true,
      handler(val) {
        if (val && val !== this.query) {
          this.query = val;
          this.doSearch();
        }
      },
    },
  },
  mounted() {
    document.addEventListener("keydown", this.handleKeydown);
    if (this.$route.query.query) {
      this.query = this.$route.query.query;
      this.doSearch();
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeydown);
    this.$store.commit("Search/clearSemanticResults");
  },
  methods: {
    handleKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        this.$refs.searchInput && this.$refs.searchInput.focus();
      }
    },
    doSearch() {
      if (!this.query) return;
      this.lastQuery = this.query;
      this.searchPerformed = true;
      this.displayCount = 20;
      this.activeFilters = { type: null, jurisdiction: null, source: null };
      this.refinementsDismissed = false;

      if (this.$route.query.query !== this.query) {
        this.$router.replace({ query: { query: this.query } });
      }

      if (this.$matomo) {
        this.$matomo.trackEvent("Search", "Semantic search", this.query);
      }

      this.$store.dispatch("Search/semanticSearch", {
        query: this.query,
        topK: 75,
      });
      this.$store.dispatch("Search/fetchRefinements", {
        query: this.query,
      });
    },
    useSuggestion(suggestion) {
      this.query = suggestion;
      this.doSearch();
    },
    useRefinement(ref) {
      const q = ref.query || ref;
      this.query = q;
      this.doSearch();
    },
    dismissRefinements() {
      this.refinementsDismissed = true;
      this.$store.commit("Search/setRefinements", []);
    },
    toggleFilter(facetType, value) {
      if (this.activeFilters[facetType] === value) {
        this.activeFilters[facetType] = null;
      } else {
        this.activeFilters[facetType] = value;
      }
      this.displayCount = 20;
    },
    loadMore() {
      this.displayCount += 20;
    },
    scorePercent(score) {
      if (!score) return 0;
      return Math.round((score / this.maxScore) * 100);
    },
    formatContentType(ct) {
      if (!ct) return "Unknown";
      const map = {
        country_report: "Country Report",
        gc_country_report: "Country Report",
        regulatory_analysis: "Regulatory Analysis",
        insight: "Insight",
        gc_insight: "Insight",
        regulatory_update: "Regulatory Update",
        gc_regulatory_update: "Regulatory Update",
        requirement: "Requirement",
        tc_requirement: "Requirement",
        gc_research_report: "Research Report",
        gc_data_forecast: "Data Forecast",
        legal_doc: "Legal Document",
        legal_doc_section: "Legal Document",
        research: "Research",
        guide: "Guide",
        article: "Article",
      };
      return map[ct] || ct.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatSource(src) {
      if (!src) return "";
      const map = {
        analyst: "Analyst",
        ai_generated: "AI Generated",
        legislation: "Legislation",
      };
      return map[src] || src.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toISOString().split("T")[0];
    },
    localUrl(url) {
      if (!url) return "/";
      try {
        const parsed = new URL(url);
        return parsed.pathname + parsed.search + parsed.hash;
      } catch (e) {
        return url;
      }
    },
    refineColor(idx) {
      const colors = ["corporate", "teal", "green", "blue", "accent"];
      return colors[idx % colors.length];
    },
  },
};
</script>

<style lang="scss" scoped>
/* ── Vixio brand tokens ── */
$corporate: #262053;
$bright-blue: #37b8cb;
$bright-green: #86c79c;
$text-primary: #1f2a44;
$text-secondary: #768692;
$border: #e2e6ea;
$border-light: #f0f2f4;
$bg-surface: #ffffff;
$bg-muted: #f7f8fa;
$link-blue: #0077c1;
$error-red: #da291c;
$amber: #d97706;
$amber-light: #fef3c7;

.search-page {
  padding: 3rem 0;
  min-height: 80vh;
  background: $bg-muted;

  &__container {
    max-width: 840px;
    margin: 0 auto;
    padding: 0 2rem;
  }
}

/* ── Search bar ── */
.search-bar {
  display: flex;
  align-items: center;
  background: $bg-surface;
  border: 2px solid $border;
  border-radius: 12px;
  padding: 0 1.6rem;
  height: 56px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: $bright-blue;
    box-shadow: 0 0 0 3px rgba($bright-blue, 0.15);
  }

  &__icon {
    flex-shrink: 0;
    margin-right: 1.2rem;
    color: $text-secondary;
  }

  &__input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 1.6rem;
    color: $text-primary;
    background: transparent;
    &::placeholder { color: darken($text-secondary, 0%); }
  }

  &__shortcut {
    flex-shrink: 0;
    display: flex;
    gap: 4px;
    kbd {
      display: inline-block;
      padding: 2px 6px;
      font-size: 1.1rem;
      color: $text-secondary;
      background: $bg-muted;
      border: 1px solid $border;
      border-radius: 4px;
      font-family: inherit;
    }
  }
}

/* ── Suggestion pills ── */
.suggestion-pills {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 1.4rem;
}

.suggestion-pill {
  padding: 7px 16px;
  border: 1px solid $border;
  border-radius: 20px;
  background: $bg-surface;
  font-size: 1.3rem;
  color: $text-primary;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: $bg-muted;
    border-color: $bright-blue;
    color: $corporate;
  }
}

/* ── Refinement section ── */
.refinement-section {
  margin-top: 2rem;
  background: $bg-surface;
  border: 1px solid $border;
  border-radius: 12px;
  overflow: hidden;
}

.refinement-header {
  display: flex;
  align-items: center;
  padding: 1.2rem 1.6rem;
  cursor: pointer;
  gap: 8px;
  user-select: none;

  &__icon { font-size: 1.4rem; }
  &__title { font-weight: 600; font-size: 1.4rem; color: $text-primary; }
  &__subtitle { font-size: 1.2rem; color: $text-secondary; flex: 1; }
  &__toggle {
    background: none; border: none; cursor: pointer;
    color: $text-secondary; padding: 4px;
    svg { transition: transform 0.2s; }
    svg.is-open { transform: rotate(180deg); }
  }
  &__close {
    background: none; border: none; cursor: pointer;
    color: $text-secondary; padding: 4px;
    &:hover { color: $text-primary; }
  }
}

.refinement-cards {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 1.6rem 1.6rem;
}

.refinement-card {
  display: flex;
  align-items: center;
  padding: 1rem 1.2rem;
  background: $bg-muted;
  border: 1px solid $border-light;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  gap: 12px;

  &:hover {
    border-color: $bright-blue;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }

  &__indicator {
    width: 4px;
    height: 32px;
    border-radius: 2px;
    flex-shrink: 0;

    &--corporate { background: $corporate; }
    &--teal { background: $bright-blue; }
    &--green { background: $bright-green; }
    &--blue { background: $link-blue; }
    &--accent { background: darken($bright-green, 15%); }
  }

  &__content { flex: 1; min-width: 0; }

  &__label {
    font-size: 1rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: $text-secondary;
    margin-bottom: 2px;
  }

  &__query {
    font-size: 1.3rem;
    color: $text-primary;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__action {
    flex-shrink: 0;
    color: $text-secondary;
    opacity: 0.4;
    transition: opacity 0.15s;
  }

  &:hover &__action { opacity: 0.8; }
}

/* Slide transition */
.slide-enter-active, .slide-leave-active {
  transition: all 0.25s ease;
  max-height: 400px;
  overflow: hidden;
}
.slide-enter, .slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* ── Facets ── */
.facet-section {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.facet-row {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 6px;

  &__label {
    font-size: 1.2rem;
    font-weight: 600;
    color: $text-secondary;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    line-height: 28px;
  }

  &__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
}

.facet-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 14px;
  border: 1px solid $border;
  border-radius: 18px;
  background: $bg-surface;
  font-size: 1.2rem;
  color: $text-primary;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { border-color: $bright-blue; }

  &--active {
    background: $corporate;
    color: #fff;
    border-color: $corporate;
    &:hover { background: lighten($corporate, 8%); }
  }

  &--more {
    color: $text-secondary;
    border-style: dashed;
    &:hover { color: $text-primary; }
  }

  &__count {
    font-size: 1.1rem;
    color: $text-secondary;
    font-weight: 600;
    .facet-pill--active & { color: rgba(255, 255, 255, 0.7); }
  }
}

/* ── Results header ── */
.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2.4rem;
  padding-bottom: 1.2rem;

  &__count {
    font-size: 1.4rem;
    color: $text-primary;
    strong { color: $corporate; }
  }

  &__locked {
    margin-left: 8px;
    color: $amber;
    strong { color: darken($amber, 10%); }
  }

  &__sort { display: flex; gap: 4px; }
}

.sort-btn {
  padding: 6px 16px;
  border: 1px solid transparent;
  border-radius: 20px;
  background: transparent;
  font-size: 1.3rem;
  color: $text-secondary;
  cursor: pointer;
  transition: all 0.15s;
  font-weight: 500;

  &:hover { background: $bg-surface; border-color: $border; }

  &--active {
    background: $corporate;
    color: #fff;
    border-color: $corporate;
  }
}

/* ── Loading ── */
/* ── Skeleton loading ── */
.skeleton-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 2rem;
}

.skeleton-card {
  background: $bg-surface;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 1.8rem 2rem;
  animation: skeleton-pulse 1.5s ease-in-out infinite;

  &__badges {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }
}

.skeleton-line {
  display: block;
  background: $bg-muted;
  border-radius: 4px;
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* ── Result cards ── */
.results-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.result-card {
  background: $bg-surface;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 1.8rem 2rem;
  transition: box-shadow 0.15s, border-color 0.15s;

  &:hover {
    border-color: darken($border, 8%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  &--blocked {
    border-color: darken($amber-light, 10%);
    background: lighten($amber-light, 4%);
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    flex-wrap: wrap;
    font-size: 1.2rem;
  }

  &__type {
    font-weight: 600;
    color: $text-secondary;
  }

  &__source {
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 1.1rem;

    &--analyst {
      border: 1px solid $bright-blue;
      color: darken($bright-blue, 15%);
      background: rgba($bright-blue, 0.08);
    }
    &--ai_generated {
      border: 1px solid $amber;
      color: $amber;
      background: rgba($amber, 0.06);
    }
    &--legislation {
      border: 1px solid $border;
      color: $text-secondary;
    }
  }

  &__jurisdiction {
    color: $text-secondary;
    margin-left: auto;
    font-size: 1.2rem;
  }

  &__title {
    display: block;
    font-size: 1.6rem;
    font-weight: 600;
    color: $corporate;
    text-decoration: none;
    line-height: 1.4;
    margin-bottom: 6px;

    &:hover { color: $bright-blue; }
    &--blocked { color: $text-secondary; cursor: default; &:hover { color: $text-secondary; } }
  }

  &__summary {
    font-size: 1.3rem;
    color: $text-secondary;
    line-height: 1.6;
    margin: 0 0 10px;

    &--blurred .blur-text {
      filter: blur(4px);
      user-select: none;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__score-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__date {
    font-size: 1.2rem;
    color: $text-secondary;
  }

  &__icon {
    flex-shrink: 0;

    &--lock { color: $amber; }
    &--arrow { color: $border; }
  }

  &:hover &__icon--arrow { color: $bright-blue; }

  &__upsell {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba($amber, 0.2);
    font-size: 1.2rem;
    color: darken($amber, 10%);

    strong { color: darken($amber, 20%); text-transform: capitalize; }

    &-text { flex: 1; }

    &-badge {
      flex-shrink: 0;
      padding: 2px 10px;
      border: 1px solid rgba($bright-blue, 0.3);
      background: rgba($bright-blue, 0.05);
      border-radius: 12px;
      font-size: 1.1rem;
      font-weight: 600;
      color: $bright-blue;
    }
  }
}

/* ── Score bar ── */
.score-bar {
  width: 100px;
  height: 5px;
  background: $border;
  border-radius: 3px;
  overflow: hidden;

  &__fill {
    height: 100%;
    background: linear-gradient(90deg, $bright-blue, $corporate);
    border-radius: 3px;
    transition: width 0.3s;

    &--blocked {
      background: $amber;
    }
  }

  &__label {
    font-size: 1.1rem;
    color: $text-secondary;
    font-weight: 500;
  }
}

/* ── Load more ── */
.load-more {
  text-align: center;
  padding: 2.4rem 0;

  &__btn {
    padding: 10px 28px;
    border: 1px solid $border;
    border-radius: 8px;
    background: $bg-surface;
    font-size: 1.4rem;
    color: $text-primary;
    cursor: pointer;
    transition: all 0.15s;
    font-weight: 500;

    &:hover {
      border-color: $bright-blue;
      color: $corporate;
    }
  }
}

/* ── No results ── */
.no-results {
  text-align: center;
  padding: 5rem 0;
  color: $text-secondary;
  font-size: 1.4rem;
  p { margin: 0.5rem 0; }
  strong { color: $text-primary; }
}

@media print {
  .search-bar, .suggestion-pills, .refinement-section,
  .facet-section, .results-header__sort, .load-more { display: none !important; }
}
</style>
